import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ChildTraitValueModel, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateVariantSelection from "@SupplyLand/Handlers/SupplyLandUpdateVariantSelection";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps> & { optionKeyPart: string } & {
    connectorTrait: ChildTraitValueModel;
} & { childTraits: ChildTraitValueModel[] } & { isTable: boolean } & ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    const tempVariantSelection = state.pages.productDetails.variantSelection;
    const variantChildren = getVariantChildrenDataView(state, ownProps.parentProductId).value;
    const variantSelection: SafeDictionary<string> = {};
    for (const [key, value] of Object.entries(tempVariantSelection)) {
        variantChildren?.forEach(child => {
            variantSelection[key] = value;
        });
    }

    return {
        variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits,
        variantChildren,
        variantSelection,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        variantProductState: getProductState(state, state.pages.productDetails.selectedProductId),
        selectedProductId: state.pages.productDetails.selectedProductId,
        parentProductState: getProductState(state, ownProps.parentProductId),
    };
};

const mapDispatchToProps = {
    updateVariantSelection,
};

export interface ProductDetailsGrandParentVariantOptionsStyles {
    wrapper?: InjectableCss;
    select?: SelectProps;
    gridItem?: GridItemProps;
}

export const grandParentVariantOptionsStyles: ProductDetailsGrandParentVariantOptionsStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    select: {
        cssOverrides: {
            formField: css``,
        },
    },
    gridItem: {
        width: 12,
        css: css`
            padding: 5px;
            justify-content: center;
            align-items: baseline;
            width: inherit;
            padding-top: 0px !important;
            padding-bottom: 0px !important;
            padding-left: 0px !importaint;
            margin-left: 15px;
        `,
    },
};

const styles = grandParentVariantOptionsStyles;

const ProductDetailsGrandParentVariantOptions: React.FC<Props> = ({
    variantChildren,
    variantSelection,
    updateVariantSelection,
    variantTraits,
    parentProductId,
    variantProductState,
    selectedProductId,
    optionKeyPart,
    connectorTrait,
    childTraits,
    isTable,
}) => {
    useEffect(() => {
        if (
            variantProductState.value &&
            (variantProductState.value.isVariantParent || variantProductState.value.childTraitValues)
        ) {
            window.history.replaceState(null, "", variantProductState.value.canonicalUrl);
        }
    }, [selectedProductId]);

    const variantChangeHandler = (traitValueId: string, variantTraitId: string, optionKeyPart: string) => {
        const grandchildSelection = variantTraitId + optionKeyPart;

        updateVariantSelection({ traitValueId, variantTraitId, productId: parentProductId, connectorTrait });
        updateVariantSelection({
            traitValueId,
            variantTraitId: grandchildSelection,
            productId: parentProductId,
            connectorTrait,
        });
    };

    if (!variantTraits || variantTraits.length === 0 || !variantChildren) {
        return null;
    }

    const filteredVariantTraits = variantTraits.filter(x => x.nameDisplay !== "Parent Child Connector");
    const traitsCopy = cloneDeep(filteredVariantTraits.slice());
    traitsCopy.forEach(variantTrait => {
        variantTrait.traitValues = variantTrait.traitValues!.slice().sort((a, b) => a.sortOrder - b.sortOrder);
    });

    const sortedTraits = traitsCopy.slice().sort((a, b) => a.sortOrder - b.sortOrder);

    sortedTraits.forEach(trait => {
        trait.traitValues?.forEach(traitValue => {
            const matchingValue = childTraits.find(ct => ct.value === traitValue.value);
            if (!matchingValue) {
                trait.traitValues = trait.traitValues?.filter(item => item !== traitValue) ?? null;
            }
        });
    });

    const getOptions = (variantTrait: VariantTraitModel) => {
        if (isTable) {
            return (
                <DataTableCell key={variantTrait.id}>
                    <Select
                        {...styles.select}
                        key={variantTrait.id}
                        value={variantSelection[variantTrait.id + optionKeyPart]}
                        onChange={event => {
                            variantChangeHandler(event.currentTarget.value, variantTrait.id, optionKeyPart);
                        }}
                        data-test-selector={`styleSelect_${variantTrait.name}`}
                    >
                        <option value="">
                            {variantTrait.unselectedValue
                                ? variantTrait.unselectedValue
                                : `${translate("Select")} ${variantTrait.nameDisplay}`}
                        </option>
                        {variantTrait.traitValues?.map((traitValue, index) => (
                            <option value={`${traitValue.id}`} key={`${traitValue.id + index}`}>
                                {traitValue.valueDisplay}
                            </option>
                        ))}
                    </Select>
                </DataTableCell>
            );
        }

        return (
            <GridItem {...styles.gridItem} key={variantTrait.id}>
                <Select
                    {...styles.select}
                    key={variantTrait.id}
                    value={variantSelection[variantTrait.id + optionKeyPart]}
                    onChange={event => {
                        variantChangeHandler(event.currentTarget.value, variantTrait.id, optionKeyPart);
                    }}
                    data-test-selector={`styleSelect_${variantTrait.name}`}
                >
                    <option value="">
                        {variantTrait.unselectedValue
                            ? variantTrait.unselectedValue
                            : `${translate("Select")} ${variantTrait.nameDisplay}`}
                    </option>
                    {variantTrait.traitValues?.map((traitValue, index) => (
                        <option value={`${traitValue.id}`} key={`${traitValue.id + index}`}>
                            {traitValue.valueDisplay}
                        </option>
                    ))}
                </Select>
            </GridItem>
        );
    };

    return <>{sortedTraits.map(variantTrait => !!variantTrait.traitValues?.length && getOptions(variantTrait))}</>;
};

export default withParentProductId(
    connect(mapStateToProps, mapDispatchToProps)(ProductDetailsGrandParentVariantOptions),
);
